import {namespace} from 'vuex-class';
import {Module} from 'vuex';
import {RootState} from '@/types';
import Auth from '@/api/Auth';
import {
  SET_USER,
  AUTHENTICATED,
  UNAUTHENTICATED,
  AUTH_LOGOUT,
  AUTH_ME_REQUEST,
  AUTH_ME_SUCCESS,
  AUTH_ME_FAILED,
  AUTH_LOGIN_FAILED,
  SET_PASSWORD,
  SET_VERIFICATION,
} from '@/store/mutationTypes';
import TokenStorage from '@/utils/Token';

export const authModule = namespace('auth');

export interface AuthCredentials {
  employee_id: string;
  password: string;
}

export interface AuthState {
  authenticated: boolean;
  user: null;
  verifying: boolean;
  error: string | null;
  password?: null;
  verification?: null;
}

export default <Module<AuthState, RootState>>{
  namespaced: true,
  state: {
    authenticated: TokenStorage.exists(),
    user: null,
    verifying: false,
    error: null,
    password: null,
    verification: null,
  },
  mutations: {
    [SET_VERIFICATION](state, val) {
      state.verification = val;
    },
    [AUTHENTICATED](state) {
      state.authenticated = true;
    },
    [UNAUTHENTICATED](state) {
      state.authenticated = false;
    },
    [SET_USER](state, user) {
      state.user = user;
    },
    [AUTH_LOGOUT](state) {
      state.authenticated = false;
      state.user = null;
    },
    [AUTH_ME_REQUEST](state: AuthState) {
      state.error = null;
      state.verifying = true;
    },
    [AUTH_ME_SUCCESS](state: AuthState, user) {
      state.verifying = false;
      state.user = user;
    },
    [AUTH_ME_FAILED](state: AuthState) {
      state.verifying = false;
      state.user = null;
      state.error = 'Unauthenticated';
    },
    [AUTH_LOGIN_FAILED](state: AuthState, message) {
      state.authenticated = false;
      state.user = null;
      state.error = message;
    },
    [SET_PASSWORD](state, password) {
      state.password = password;
    },
  },
  actions: {
    async login({commit}, credentials: AuthCredentials) {
      try {
        const res = await new Auth().login(credentials);
        const user = res.data.user;
        const token = res.data.accessToken;
        // const aclUser = new ACLUser(user);
        // if (aclUser.canAccessCMS()) {
        localStorage.setItem('USER', JSON.stringify(user));
        TokenStorage.save(token);
        commit(SET_USER, user);
        commit(AUTHENTICATED);
        // } else {
        //   const message = 'Unauthorized';
        //   commit(AUTH_LOGIN_FAILED, message);
        //   return message;
        // }
        return res;
      } catch (e) {
        return e.response.data;
      }
    },
    logout({commit}) {
      commit(AUTH_LOGOUT);
      TokenStorage.remove();
      localStorage.clear();
      return Promise.resolve(true);
    },
    async register(_, payload) {
      try {
        const data = await new Auth().register(payload);
        return data;
      } catch (e) {
        return e.response.data;
      }
    },
    async registerMail(_, payload) {
      try {
        const data = await new Auth().registerMail(payload);
        return data;
      } catch (e) {
        return e.response.data;
      }
    },
    async me({commit}) {
      commit(AUTH_ME_REQUEST);
      try {
        const res = await new Auth().me();
        const {status, data} = res;
        if (status === 200) {
          commit(AUTH_ME_SUCCESS, data.access);
          return Promise.resolve(data.access);
        } else {
          commit(AUTH_ME_FAILED, data.message);
          return Promise.reject(data.message);
        }
      } catch (e) {
        const message = e.message || e.response.data.message;
        commit(AUTH_ME_FAILED, message);
        return Promise.reject(message);
      }
    },
    async changePassword({commit}, payload: any) {
      try {
        const res = await new Auth().changePassword(payload);
        commit(SET_PASSWORD, res.data.data);
        return res;
      } catch (e) {
        return e.response.data;
      }
    },
    async getLinkresetPassword({commit}, payload: any) {
      try {
        const res = await new Auth().getLinkresetPassword(payload);
        commit(SET_PASSWORD, res.data.data);
        return res;
      } catch (e) {
        return e.response.data;
      }
    },
    async resetPassword({commit}, payload: any) {
      try {
        const res = await new Auth().resetPassword(payload);
        commit(SET_PASSWORD, res.data.data);
        return res;
      } catch (e) {
        return e.response.data;
      }
    },
    async checkVerification({commit}, params = {}) {
      try {
        const res = await new Auth().checkVerification(params);
        commit(SET_VERIFICATION, res.data);
        return res;
      } catch (e) {
        return e.response.data;
      }
    },
  },
  getters: {
    isAuthenticated: state => state.authenticated,
    user: state => state.user,
    loading: state => state.verifying,
  },
};
